import { SignalPanel } from '@/common/types';
import type { LaneHeight, ReorderableItem } from '@/common/ui';
import { AASM_BAND_FILTER_DEFAULTS, hasAdjustableBandFilter } from '@/common/utils';
import { Preset, Setting, SignalOverflow } from '@/modules/epoch/types';
import { SettingPreset, ViewMode } from './state';

export const laneHeightFromApi = (value?: number): LaneHeight =>
  value !== undefined && value !== null && value > 0
    ? { type: 'fixed', presetValue: value, localValue: value }
    : { type: 'auto' };

export const laneHeightToApi = (laneHeight?: LaneHeight): number | undefined =>
  laneHeight?.type === 'fixed' ? laneHeight.presetValue : undefined;

export const settingFromApi = (setting: Setting): ReorderableItem => ({
  name: setting.name,
  order: setting.order,
  visible: setting.visible,
  signalColor: setting.signalColor,
  lineColor: setting.lineColor ?? '#000000',
  isOverflowing: setting.isOverflowing ?? false,
  panel: setting.panel as SignalPanel | null,
  signalLaneHeight: laneHeightFromApi(setting.signalLaneHeight),
  eventColors: setting.eventColors ?? [],
  bandFilter:
    setting.bandFilter ??
    (hasAdjustableBandFilter(setting.name)
      ? { ...AASM_BAND_FILTER_DEFAULTS[setting.name] }
      : undefined)
});

export const settingToApi = (item: ReorderableItem): Setting => ({
  name: item.name,
  order: item.order,
  visible: item.visible,
  signalColor: item.signalColor,
  lineColor: item.lineColor,
  isOverflowing: item.isOverflowing,
  panel: item.panel ?? undefined,
  signalLaneHeight: laneHeightToApi(item.signalLaneHeight),
  eventColors: item.eventColors,
  bandFilter: item.bandFilter
});

export const signalsListFromApi = (settings: Setting[] = []): ReorderableItem[] =>
  settings.map(settingFromApi);

export const signalsListToApi = (items: ReorderableItem[] = []): Setting[] =>
  items.map(settingToApi);

export const presetFromApi = (preset: Preset): SettingPreset => ({
  id: preset.id,
  name: preset.name,
  signalsList: signalsListFromApi(preset.signalsList),
  hypnogramFullNightSignals: signalsListFromApi(preset.hypnogramFullNightSignals),
  fullNightSignals: preset.fullNightSignals as unknown as string[],
  signalOverflow: (preset.signalOverflow as SignalOverflow) ?? SignalOverflow.None,
  band: preset.band,
  notch: preset.notch,
  viewMode: preset.viewMode as ViewMode,
  dualTimeBase: {
    topWindowSize: preset.dualTimeBase?.topWindowSize ?? 30,
    bottomWindowSize: preset.dualTimeBase?.bottomWindowSize ?? 300,
    singleHypnogram: preset.dualTimeBase?.singleHypnogram ?? false
  },
  artifactColor: preset.artifactColor,
  shortcutKeys: preset.shortcutKeys
});

export const presetsFromApi = (presets: Preset[] = []): SettingPreset[] =>
  presets.map(presetFromApi);

export const presetToApi = (preset: SettingPreset): Preset => ({
  id: preset.id,
  name: preset.name,
  band: preset.band,
  notch: preset.notch,
  signalOverflow: preset.signalOverflow,
  signalsList: signalsListToApi(preset.signalsList),
  fullNightSignals: preset.fullNightSignals as unknown as Setting[],
  hypnogramFullNightSignals: signalsListToApi(preset.hypnogramFullNightSignals),
  viewMode: preset.viewMode,
  dualTimeBase: preset.dualTimeBase,
  artifactColor: preset.artifactColor,
  shortcutKeys: preset.shortcutKeys
});

export const presetsToApi = (presets: SettingPreset[] = []): Preset[] => presets.map(presetToApi);
